<template>
  <div class="node-go-widget">
    <div v-for="node in displayNodes" :key="node.id" class="node-go-row">
      <span class="node-go-row__name">{{ node.name }}</span>
      <span class="node-go-badge" :class="badgeClass(node)">
        <i class="fas" :class="iconClass(node)"></i>
        {{ statusLabel(node) }}
      </span>
    </div>
    <div v-if="!displayNodes.length" class="text-muted small">No nodes configured</div>

    <div class="node-go-actions">
      <button
        type="button"
        class="node-go-standby"
        :disabled="sending || !onlineTargets.length"
        @click="sendStandby"
      >
        <i class="fas" :class="sending ? 'fa-spinner fa-spin' : 'fa-hand-paper'" />
        Standby
      </button>
    </div>
    <div v-if="status" class="node-go-status" :class="ok ? 'is-ok' : 'is-err'">{{ status }}</div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

const STANDBY_ALERT = {
  message: 'Ayo set Ready',
  color: '#ffbc34',
  icon: 'hand-paper',
  promptReady: true,
}

export default {
  props: { config: { type: Object, default: () => ({}) } },
  data() {
    return {
      sending: false,
      status: '',
      ok: false,
    }
  },
  computed: {
    ...mapGetters('mission', ['nodes']),
    displayNodes() {
      const ids = this.config.nodeIds || []
      if (ids.length) return this.nodes.filter((n) => ids.includes(n.id))
      return this.nodes
    },
    onlineTargets() {
      return this.displayNodes.filter((n) => n.online)
    },
    filteredIds() {
      return this.config.nodeIds || []
    },
  },
  methods: {
    ...mapActions('mission', ['sendAlert']),
    badgeClass(node) {
      if (!node.online) return 'node-go-badge--off'
      return node.ready_status ? 'node-go-badge--go' : 'node-go-badge--hold'
    },
    iconClass(node) {
      if (!node.online) return 'fa-plug'
      return node.ready_status ? 'fa-rocket' : 'fa-hourglass-half'
    },
    statusLabel(node) {
      if (!node.online) return 'Offline'
      return node.ready_status ? 'GO' : 'Standby'
    },
    async sendStandby() {
      if (this.sending || !this.onlineTargets.length) return
      this.sending = true
      this.status = ''
      try {
        let sent = 0
        if (!this.filteredIds.length) {
          const res = await this.sendAlert({ ...STANDBY_ALERT })
          sent = res?.sent ?? 0
        } else {
          const results = await Promise.all(
            this.onlineTargets.map((n) => this.sendAlert({ ...STANDBY_ALERT, node_id: n.id })),
          )
          sent = results.reduce((sum, r) => sum + (r?.sent ?? 0), 0)
        }
        this.ok = true
        this.status = `Standby → ${sent} device${sent === 1 ? '' : 's'}`
      } catch (e) {
        this.ok = false
        this.status = e.response?.data?.message || e.message || 'Failed to send'
      } finally {
        this.sending = false
      }
    },
  },
}
</script>

<style scoped>
.node-go-widget {
  color: var(--ws-text);
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  height: 100%;
  min-height: 0;
}
.node-go-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
}
.node-go-row__name {
  font-weight: 600;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.node-go-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.35em;
  padding: 0.2em 0.55em;
  border-radius: 999px;
  font-size: 0.8em;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  flex-shrink: 0;
}
.node-go-badge--go {
  background: rgba(239, 110, 110, 0.22);
  color: #ff8e8e;
}
.node-go-badge--hold {
  background: rgba(255, 188, 52, 0.22);
  color: #ffd54f;
}
.node-go-badge--off {
  background: var(--ws-ctrl-bg, rgba(108, 117, 125, 0.15));
  color: var(--ws-text-muted, #6c757d);
}
.node-go-actions {
  margin-top: auto;
  padding-top: 0.15rem;
}
.node-go-standby {
  width: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  min-height: 34px;
  border: 1px solid rgba(255, 188, 52, 0.55);
  background: rgba(255, 188, 52, 0.18);
  color: var(--ws-text);
  border-radius: 6px;
  font-size: 0.82em;
  font-weight: 700;
  cursor: pointer;
}
.node-go-standby:hover:not(:disabled) {
  background: rgba(255, 188, 52, 0.28);
}
.node-go-standby:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.node-go-status {
  font-size: 0.72em;
}
.node-go-status.is-ok {
  color: #128c7e;
}
.node-go-status.is-err {
  color: #c0392b;
}
</style>
